import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ClipboardContent } from 'src/app/general-components/form-controls/clipboard';
import { indiOrderNumberValidator } from 'src/app/general-components/form-controls/indi-order-number';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';
import { concat, EMPTY, from, Observable, of, Subscription } from 'rxjs';
import { KrampShippingAddress } from '../kramp-shipping-addresses/kramp-shipping-address';
import { KrampShippingAddressService } from '../kramp-shipping-addresses/kramp-shipping-address.service';
import {
  KrampOrder,
  NseLookup,
  NseStatus,
  OrderShipmentInfo,
  ShipmentPackage,
  ShipToLookup,
  UnpackedLine,
} from './order-shipment-info';
import { OrderShipmentInfoService } from './order-shipment-info.service';
import {
  buildAllCopy,
  buildPackageCopy,
  buildShipToCopy,
  carrierName,
  formatShipToLine,
  hasNoShipmentInfo,
  isNse,
  nseScanText,
  NO_SHIPMENT_INFO,
  SHIP_TO_NO,
  shipToDiffersFrom,
  UNPACKED_LINES,
  unpackedLineStatus,
} from './order-shipment-info-copy';

interface KrampOrderView {
  krampOrder: KrampOrder;
  shipToDiffers: boolean;
  noShipmentInfo: boolean;
  packages: PackageView[];
  unpackedLines: UnpackedLineView[];
}

interface UnpackedLineView {
  line: UnpackedLine;
  status: string;
}

interface PackageView {
  shipmentPackage: ShipmentPackage;
  carrierName: string;
  copy: ClipboardContent;
  /** Only for NSE packages, which have no public track & trace page */
  nse: NseLookup | null;
  nseText: string | null;
}

@Component({
  selector: 'app-order-shipment-info',
  templateUrl: './order-shipment-info.component.html',
  styleUrls: ['./order-shipment-info.component.scss'],
})
export class OrderShipmentInfoComponent implements OnDestroy {
  readonly noShipmentInfo = NO_SHIPMENT_INFO;
  readonly shipToNoLabel = SHIP_TO_NO;
  readonly unpackedLinesTitle = UNPACKED_LINES;

  readonly orderNumber = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required.bind(null), indiOrderNumberValidator],
  });
  readonly form = new FormGroup({ orderNumber: this.orderNumber });

  isLoading = false;
  errorMessage: string | null = null;
  info: OrderShipmentInfo | null = null;
  krampOrders: KrampOrderView[] = [];
  copyAll: ClipboardContent | null = null;
  shipTo: ShipToLookup | null = null;
  shipToAddress: KrampShippingAddress | null = null;
  shipToLine: string | null = null;
  shipToCopy: ClipboardContent | null = null;

  private nseLookups = new Map<string, NseLookup>();
  private subscription?: Subscription;

  constructor(
    private orderShipmentInfoService: OrderShipmentInfoService,
    private krampShippingAddressService: KrampShippingAddressService
  ) {}

  search(): void {
    if (this.orderNumber.invalid) {
      this.orderNumber.markAsTouched();
      return;
    }
    this.subscription?.unsubscribe();
    this.isLoading = true;
    this.errorMessage = null;
    this.info = null;
    this.shipTo = null;
    this.nseLookups = new Map();
    this.render();

    this.subscription = this.orderShipmentInfoService
      .getOrderShipmentInfo(this.orderNumber.value.trim())
      .pipe(
        catchError((error: unknown) => {
          this.errorMessage = errorMessageFor(error);
          this.isLoading = false;
          console.error(error);
          return EMPTY;
        }),
        tap(info => {
          const nseBarcodes = nseBarcodesOf(info);
          this.info = info;
          this.shipTo = { state: 'loading' };
          this.nseLookups = new Map(nseBarcodes.map(barcode => [barcode, { state: 'loading' }]));
          this.isLoading = false;
          this.render();
        }),
        // One request after another, see "Parallel requests to private API hosts" in CLAUDE.md
        switchMap(info =>
          concat(
            this.lookUpShipTo(info.customerNumber).pipe(
              tap(shipTo => {
                this.shipTo = shipTo;
              })
            ),
            from(nseBarcodesOf(info)).pipe(
              concatMap(barcode =>
                this.lookUpNse(barcode).pipe(
                  tap(nse => {
                    this.nseLookups.set(barcode, nse);
                  })
                )
              )
            )
          )
        )
      )
      .subscribe(() => {
        this.render();
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private lookUpShipTo(customerNumber: string): Observable<ShipToLookup> {
    return this.krampShippingAddressService.getShippingAddressOfCustomer(customerNumber).pipe(
      map((address): ShipToLookup => (address ? { state: 'found', address } : { state: 'none' })),
      // Deliberately swallowed: the order and its packages are still worth showing without a ship-to number
      catchError((error: unknown) => {
        console.error(error);
        return of<ShipToLookup>({ state: 'error' });
      })
    );
  }

  private lookUpNse(barcode: string): Observable<NseLookup> {
    return this.orderShipmentInfoService.getNseStatus(barcode).pipe(
      map((nseStatus): NseLookup => ({ state: 'found', nseStatus })),
      // Deliberately swallowed: NSE being down should not hide the rest of the order
      catchError((error: unknown) => {
        console.error(error);
        return of<NseLookup>({ state: 'error' });
      })
    );
  }

  private render(): void {
    const info = this.info;
    const shipToAddress = this.shipTo?.state === 'found' ? this.shipTo.address : null;
    const nseStatuses = new Map<string, NseStatus>();
    this.nseLookups.forEach((nse, barcode) => {
      if (nse.state === 'found') {
        nseStatuses.set(barcode, nse.nseStatus);
      }
    });
    this.shipToAddress = shipToAddress;
    this.shipToLine = shipToAddress ? formatShipToLine(shipToAddress) : null;
    this.shipToCopy = shipToAddress ? buildShipToCopy(shipToAddress) : null;
    this.copyAll = info ? buildAllCopy(info, shipToAddress, nseStatuses) : null;
    this.krampOrders = info
      ? info.krampOrders.map(krampOrder => ({
          krampOrder,
          shipToDiffers: shipToAddress !== null && shipToDiffersFrom(krampOrder.deliveryAddress, shipToAddress),
          noShipmentInfo: hasNoShipmentInfo(krampOrder),
          unpackedLines: krampOrder.unpackedLines.map(line => ({ line, status: unpackedLineStatus(line) })),
          packages: krampOrder.packages.map(shipmentPackage => ({
            shipmentPackage,
            carrierName: carrierName(shipmentPackage.carrier),
            copy: buildPackageCopy(info, krampOrder, shipmentPackage, shipToAddress, nseStatuses),
            nse: this.nseLookups.get(shipmentPackage.barcode) ?? null,
            nseText: nseTextOf(nseStatuses.get(shipmentPackage.barcode)),
          })),
        }))
      : [];
  }
}

const nseTextOf = (nseStatus: NseStatus | undefined): string | null => (nseStatus ? nseScanText(nseStatus) : null);

const nseBarcodesOf = (info: OrderShipmentInfo): string[] => [
  ...new Set(
    info.krampOrders.flatMap(krampOrder =>
      krampOrder.packages.filter(isNse).map(shipmentPackage => shipmentPackage.barcode)
    )
  ),
];

const errorMessageFor = (error: unknown): string => {
  const status = error instanceof HttpErrorResponse ? error.status : undefined;
  switch (status) {
    case 404:
      return 'Order niet gevonden.';
    case 502:
    case 503:
      return 'Verzendgegevens tijdelijk niet beschikbaar';
    default:
      return 'Er is iets misgegaan bij het ophalen van de verzendgegevens';
  }
};
